/**
 * CalendarPage — Kalendarz Promocji
 * Week/Month toggle, status colors, post modal, empty state
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, ChevronLeft, ChevronRight, Plus, X,
  Facebook, Instagram, Clock, Loader2, Trash2,
  Pencil, Send, CalendarDays, LayoutGrid, CheckCircle2, AlertCircle, Timer
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PanelLayout from '@/components/PanelLayout';
import AssistantPanel from '@/components/AssistantPanel';
import { API } from '@/utils/api';
import { useTranslation } from 'react-i18next';

/* ============ HELPERS ============ */
const DAY_NAMES_SHORT = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Nd'];
const MONTH_NAMES = ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'];

const STATUS_CONFIG = {
  scheduled:  { label: 'Zaplanowany',   color: '#10B981', bg: '#ECFDF5', icon: CheckCircle2 },
  planned:    { label: 'Zaplanowany',   color: '#10B981', bg: '#ECFDF5', icon: CheckCircle2 },
  published:  { label: 'Opublikowany',  color: '#3B82F6', bg: '#EFF6FF', icon: CheckCircle2 },
  publishing: { label: 'W trakcie',     color: '#F59E0B', bg: '#FFFBEB', icon: Timer },
  failed:     { label: 'Błąd',          color: '#EF4444', bg: '#FEF2F2', icon: AlertCircle },
};

const PLATFORM_CONFIG = {
  facebook:  { label: 'Facebook',  short: 'FB', color: '#1877F2', icon: Facebook },
  instagram: { label: 'Instagram', short: 'IG', color: '#E4405F', icon: Instagram },
};

function getMonday(d) {
  const dt = new Date(d);
  const day = dt.getDay();
  const diff = dt.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(dt.setDate(diff));
}

function formatDate(d) {
  return d.toISOString().split('T')[0];
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function isSameDay(d1, d2) {
  return formatDate(d1) === formatDate(d2);
}

function isToday(dateStr) {
  return dateStr === formatDate(new Date());
}

/* ============ PLATFORM BADGE ============ */
function PlatformBadge({ platform }) {
  const cfg = PLATFORM_CONFIG[platform] || PLATFORM_CONFIG.facebook;
  const Icon = cfg.icon;
  return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold text-white" style={{ background: cfg.color }}>
      <Icon className="w-2.5 h-2.5" /> {cfg.short}
    </span>
  );
}

/* ============ STATUS DOT ============ */
function StatusDot({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.scheduled;
  return <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{ background: cfg.color }} />;
}

/* ============ EVENT CARD (inside day cell) ============ */
function EventCard({ event, onClick }) {
  const cfg = STATUS_CONFIG[event.status] || STATUS_CONFIG.scheduled;
  return (
    <button
      onClick={() => onClick(event)}
      className="w-full text-left px-2 py-1.5 rounded-lg transition-all hover:scale-[1.02] hover:shadow-sm border-l-[3px] mb-1"
      style={{ background: cfg.bg, borderLeftColor: cfg.color }}
      data-testid={`cal-event-${event.id}`}
    >
      <div className="flex items-center gap-1 mb-0.5">
        <PlatformBadge platform={event.platform} />
        <span className="text-[10px] font-medium text-zinc-500">{event.time}</span>
      </div>
      <p className="text-[11px] font-medium text-zinc-800 line-clamp-2 leading-tight">{event.title}</p>
    </button>
  );
}

/* ============ POST DETAIL MODAL ============ */
function PostModal({ event, onClose, onDelete, onPublishNow }) {
  const sCfg = STATUS_CONFIG[event.status] || STATUS_CONFIG.scheduled;
  const SIcon = sCfg.icon;
  const [deleting, setDeleting] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    await onDelete(event);
    setDeleting(false);
    onClose();
  };

  const handlePublish = async () => {
    setPublishing(true);
    await onPublishNow(event);
    setPublishing(false);
    onClose();
  };

  const formattedDate = (() => {
    try {
      const d = new Date(event.date + 'T00:00:00');
      return d.toLocaleDateString('pl-PL', { day: 'numeric', month: 'long' }) + ', ' + event.time;
    } catch { return event.date + ' ' + event.time; }
  })();

  return (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4" onClick={onClose} data-testid="post-modal-overlay">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden"
        data-testid="post-modal"
      >
        {/* Image */}
        {event.image_url && (
          <div className="h-48 overflow-hidden relative">
            <img src={event.image_url} alt="" className="w-full h-full object-cover" />
            <div className="absolute top-3 left-3">
              <PlatformBadge platform={event.platform} />
            </div>
          </div>
        )}

        <div className="p-5">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-zinc-900">Szczegóły publikacji</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-zinc-500">
                  {(PLATFORM_CONFIG[event.platform] || PLATFORM_CONFIG.facebook).label}
                </span>
                <span className="text-gray-300">·</span>
                <span className="text-sm text-zinc-500">{formattedDate}</span>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-zinc-100">
              <X className="w-5 h-5 text-zinc-400" />
            </button>
          </div>

          {/* Status badge */}
          <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-xl" style={{ background: sCfg.bg }}>
            <SIcon className="w-4 h-4" style={{ color: sCfg.color }} />
            <span className="text-sm font-semibold" style={{ color: sCfg.color }}>{sCfg.label}</span>
            {event.error_message && <span className="text-xs text-red-500 ml-auto">{event.error_message}</span>}
          </div>

          {/* Content */}
          <div className="mb-4 p-3 bg-[#F8F9FA] rounded-xl">
            <p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap">{event.message || 'Brak treści'}</p>
          </div>

          {/* Source campaign / page info */}
          <div className="flex items-center justify-between mb-4 text-xs text-zinc-400">
            {event.page_name && <span>Strona: {event.page_name}</span>}
            <a href="/panel/booster" className="text-[#F47A3B] font-medium hover:underline" data-testid="modal-open-campaign">
              Otwórz kampanię
            </a>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            {(event.status === 'scheduled' || event.status === 'planned') && (
              <Button
                onClick={handlePublish}
                disabled={publishing}
                className="flex-1 rounded-xl h-10 text-white font-semibold text-sm"
                style={{ background: '#F47A3B' }}
                data-testid="modal-publish-btn"
              >
                {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                Opublikuj teraz
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => { onClose(); window.location.href = '/panel/booster'; }}
              className="flex-1 rounded-xl h-10 text-sm"
              data-testid="modal-edit-btn"
            >
              <Pencil className="w-4 h-4 mr-1" /> Edytuj
            </Button>
            {(event.status === 'scheduled' || event.status === 'planned') && (
              <Button
                variant="outline"
                onClick={handleDelete}
                disabled={deleting}
                className="rounded-xl h-10 text-sm px-3 text-red-500 border-red-200 hover:bg-red-50"
                data-testid="modal-delete-btn"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/* ============ MAIN COMPONENT ============ */
export default function CalendarPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [view, setView] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState({ scheduled: 0, published: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  /* ---- Date range calculation ---- */
  const { startDate, endDate, days } = useMemo(() => {
    if (view === 'week') {
      const mon = getMonday(currentDate);
      const sun = addDays(mon, 6);
      const dayList = [];
      for (let i = 0; i < 7; i++) dayList.push(addDays(mon, i));
      return { startDate: mon, endDate: sun, days: dayList };
    } else {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const first = new Date(year, month, 1);
      const last = new Date(year, month + 1, 0);
      const startMon = getMonday(first);
      const dayList = [];
      let d = new Date(startMon);
      while (d <= last || d.getDay() !== 1) {
        dayList.push(new Date(d));
        d = addDays(d, 1);
        if (dayList.length >= 42) break;
      }
      return { startDate: startMon, endDate: dayList[dayList.length - 1], days: dayList };
    }
  }, [view, currentDate]);

  /* ---- Fetch events ---- */
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/calendar/events`, {
        params: { start: formatDate(startDate), end: formatDate(endDate) },
        headers,
      });
      setEvents(res.data.events || []);
      setSummary(res.data.summary || {});
    } catch (err) {
      console.error('Calendar fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  /* ---- Navigation ---- */
  const goNext = () => {
    if (view === 'week') setCurrentDate(addDays(currentDate, 7));
    else setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };
  const goPrev = () => {
    if (view === 'week') setCurrentDate(addDays(currentDate, -7));
    else setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };
  const goToday = () => setCurrentDate(new Date());

  /* ---- Actions ---- */
  const handleDelete = async (event) => {
    try {
      if (event.source === 'booster') {
        await axios.delete(`${API}/booster/schedule/${event.id}`, { headers });
      } else {
        await axios.delete(`${API}/booster/auto-marketing/queue/${event.id}`, { headers });
      }
      toast.success('Post usunięty z kalendarza');
      fetchEvents();
    } catch (err) {
      toast.error('Nie udało się usunąć postu');
    }
  };

  const handlePublishNow = async (event) => {
    toast.info('Publikowanie posta...');
    // For now, redirect to Booster for manual publish
    navigate('/panel/booster');
  };

  /* ---- Events grouped by date ---- */
  const eventsByDate = useMemo(() => {
    const map = {};
    for (const ev of events) {
      if (!map[ev.date]) map[ev.date] = [];
      map[ev.date].push(ev);
    }
    return map;
  }, [events]);

  /* ---- Period label ---- */
  const periodLabel = view === 'week'
    ? (() => {
        const mon = getMonday(currentDate);
        const sun = addDays(mon, 6);
        const mStr = mon.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });
        const sStr = sun.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short', year: 'numeric' });
        return `${mStr} — ${sStr}`;
      })()
    : `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`;

  const totalThisWeek = summary.scheduled + summary.published + summary.failed;

  return (
    <PanelLayout>
      <div className="max-w-[1200px] mx-auto" data-testid="calendar-page">

        {/* ============ HEADER ============ */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F47A3B]/10 border border-[#F47A3B]/15 mb-4">
            <Calendar className="w-3.5 h-3.5 text-[#F47A3B]" />
            <span className="text-[12px] font-semibold text-[#F47A3B]">Twój plan działania</span>
          </div>
          <h1 className="text-3xl font-extrabold text-zinc-900 mb-2">Kalendarz promocji</h1>
          <p className="text-[15px] text-zinc-500 max-w-md mx-auto">Zobacz, co i kiedy trafia do klientów</p>
        </div>

        {/* ============ MINI STATUS BAR — spokój i zaufanie ============ */}
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border bg-white px-5 py-3 mb-4 flex items-center justify-between"
          style={{ borderColor: totalThisWeek > 0 ? '#10B98140' : '#E8EAED', background: totalThisWeek > 0 ? '#F0FDF4' : 'white' }}
          data-testid="status-bar"
        >
          <div className="flex items-center gap-2.5">
            <div className={`w-3 h-3 rounded-full ${totalThisWeek > 0 ? 'bg-[#10B981] animate-pulse' : 'bg-[#F47A3B]'}`} />
            <span className="text-sm font-semibold text-zinc-900">
              {totalThisWeek > 0 ? 'Twoja promocja działa' : 'Twój marketing jest gotowy do startu'}
            </span>
          </div>
          <div className="flex items-center gap-4 text-xs">
            {summary.scheduled > 0 && (
              <span className="flex items-center gap-1.5 font-semibold text-[#10B981]">
                <span className="w-2 h-2 rounded-full bg-[#10B981]" />
                {summary.scheduled} {summary.scheduled === 1 ? 'post' : 'posty'} w tym tygodniu
              </span>
            )}
            {summary.published > 0 && (
              <span className="flex items-center gap-1.5 font-medium text-[#3B82F6]">
                <span className="w-2 h-2 rounded-full bg-[#3B82F6]" />
                {summary.published} opublikowanych
              </span>
            )}
            {summary.failed > 0 && (
              <span className="flex items-center gap-1.5 font-medium text-[#EF4444]">
                <span className="w-2 h-2 rounded-full bg-[#EF4444]" />
                {summary.failed} z błędem
              </span>
            )}
            {totalThisWeek === 0 && (
              <span className="flex items-center gap-1.5 font-medium text-[#F47A3B]">
                Uruchom kampanię, aby zobaczyć plan
              </span>
            )}
          </div>
        </motion.div>

        {/* ============ 3-COLUMN LAYOUT ============ */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">

          {/* CENTER COLUMN — Calendar */}
          <div className="lg:col-span-8">

        {/* ============ VIEW SWITCH + NAVIGATION ============ */}
        <div className="flex items-center justify-between mb-4">
          {/* View toggle */}
          <div className="flex bg-[#F3F4F6] rounded-xl p-1" data-testid="view-toggle">
            <button
              onClick={() => setView('week')}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                view === 'week' ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-500 hover:text-zinc-900'
              }`}
              data-testid="view-week"
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Tydzień
            </button>
            <button
              onClick={() => setView('month')}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                view === 'month' ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-500 hover:text-zinc-900'
              }`}
              data-testid="view-month"
            >
              <CalendarDays className="w-3.5 h-3.5" /> Miesiąc
            </button>
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-2">
            <button onClick={goToday} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#F3F4F6] text-zinc-500 hover:bg-[#E8EAED] transition" data-testid="go-today">
              Dziś
            </button>
            <button onClick={goPrev} className="p-1.5 rounded-lg hover:bg-[#F3F4F6] transition" data-testid="go-prev">
              <ChevronLeft className="w-5 h-5 text-zinc-500" />
            </button>
            <span className="text-sm font-semibold text-zinc-900 min-w-[180px] text-center" data-testid="period-label">
              {periodLabel}
            </span>
            <button onClick={goNext} className="p-1.5 rounded-lg hover:bg-[#F3F4F6] transition" data-testid="go-next">
              <ChevronRight className="w-5 h-5 text-zinc-500" />
            </button>
          </div>
        </div>

        {/* ============ CALENDAR GRID ============ */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-6 h-6 text-[#F47A3B] animate-spin" />
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-2xl border border-zinc-200 overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.04)] relative"
            data-testid="calendar-grid"
          >
            {/* Day headers */}
            <div className="grid grid-cols-7 border-b border-zinc-200">
              {DAY_NAMES_SHORT.map((name) => (
                <div key={name} className="px-2 py-2.5 text-center text-xs font-semibold text-zinc-500 border-r border-zinc-200 last:border-r-0">
                  {name}
                </div>
              ))}
            </div>

            {/* Day cells */}
            {view === 'week' ? (
              <div className="grid grid-cols-7 min-h-[420px]">
                {days.map((day) => {
                  const dateStr = formatDate(day);
                  const dayEvents = eventsByDate[dateStr] || [];
                  const today = isToday(dateStr);
                  return (
                    <div
                      key={dateStr}
                      className={`border-r border-zinc-200 last:border-r-0 p-2 flex flex-col ${today ? 'bg-[#FFF8F3]' : ''}`}
                      data-testid={`day-${dateStr}`}
                    >
                      <div className="text-center mb-2">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                          today ? 'bg-[#F47A3B] text-white' : 'text-zinc-900'
                        }`}>
                          {day.getDate()}
                        </span>
                      </div>
                      <div className="flex-1 space-y-1 overflow-y-auto max-h-[350px]">
                        {dayEvents.map(ev => (
                          <EventCard key={ev.id} event={ev} onClick={setSelectedEvent} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Month view */
              <div>
                {Array.from({ length: Math.ceil(days.length / 7) }, (_, rowIdx) => (
                  <div key={rowIdx} className="grid grid-cols-7 border-b border-zinc-200 last:border-b-0">
                    {days.slice(rowIdx * 7, rowIdx * 7 + 7).map((day) => {
                      const dateStr = formatDate(day);
                      const dayEvents = eventsByDate[dateStr] || [];
                      const today = isToday(dateStr);
                      const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                      return (
                        <div
                          key={dateStr}
                          className={`border-r border-zinc-200 last:border-r-0 p-1.5 min-h-[100px] ${
                            today ? 'bg-[#FFF8F3]' : !isCurrentMonth ? 'bg-[#FAFAFA]' : ''
                          }`}
                          data-testid={`day-${dateStr}`}
                        >
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold mb-1 ${
                            today ? 'bg-[#F47A3B] text-white' : isCurrentMonth ? 'text-zinc-900' : 'text-[#C4C9D1]'
                          }`}>
                            {day.getDate()}
                          </span>
                          <div className="space-y-0.5 overflow-y-auto max-h-[70px]">
                            {dayEvents.map(ev => (
                              <button
                                key={ev.id}
                                onClick={() => setSelectedEvent(ev)}
                                className="w-full text-left px-1 py-0.5 rounded text-[9px] font-medium truncate flex items-center gap-1"
                                style={{ background: (STATUS_CONFIG[ev.status] || STATUS_CONFIG.scheduled).bg, color: (STATUS_CONFIG[ev.status] || STATUS_CONFIG.scheduled).color }}
                                data-testid={`cal-event-${ev.id}`}
                              >
                                <StatusDot status={ev.status} />
                                <span className="truncate">{ev.time} {(PLATFORM_CONFIG[ev.platform] || {}).short || 'FB'}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}

            {/* ============ EMPTY OVERLAY — NEVER empty grid ============ */}
            {events.length === 0 && (
              <div className="absolute inset-0 top-[40px] flex items-center justify-center bg-white/80 backdrop-blur-[2px] z-10" data-testid="empty-overlay">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center max-w-md px-8"
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#F47A3B]/10 flex items-center justify-center mx-auto mb-4">
                    <Calendar className="w-7 h-7 text-[#F47A3B]" />
                  </div>
                  <h3 className="text-lg font-bold text-zinc-900 mb-2">
                    Masz już gotowy plan promocji
                  </h3>
                  <p className="text-sm text-zinc-500 mb-5">
                    Uruchom kampanię i zobacz, jak rozkładamy posty na cały tydzień
                  </p>
                  <Button
                    onClick={() => navigate('/panel/booster')}
                    className="rounded-xl h-11 px-8 text-white font-semibold"
                    style={{ background: '#F47A3B' }}
                    data-testid="empty-cta"
                  >
                    <Plus className="w-4 h-4 mr-1.5" /> Uruchom promocję
                  </Button>
                </motion.div>
              </div>
            )}
          </motion.div>
        )}

        {/* ============ STATUS LEGEND ============ */}
        {events.length > 0 && (
          <div className="flex items-center gap-5 mt-4 px-1" data-testid="status-legend">
            {[
              { key: 'scheduled', ...STATUS_CONFIG.scheduled },
              { key: 'published', ...STATUS_CONFIG.published },
              { key: 'publishing', ...STATUS_CONFIG.publishing },
              { key: 'failed', ...STATUS_CONFIG.failed },
            ].map(cfg => (
              <div key={cfg.key} className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: cfg.color }} />
                <span className="text-xs text-zinc-500">{cfg.label}</span>
              </div>
            ))}
          </div>
        )}

          </div>

          {/* RIGHT COLUMN — Assistant */}
          <div className="lg:col-span-4" data-testid="calendar-right-column">
            <div className="sticky top-[80px]">
              <AssistantPanel
                whatNow="Zaplanowane treści pomagają regularnie docierać do klientów."
              />
            </div>
          </div>

        </div>
      </div>

      {/* ============ POST MODAL ============ */}
      <AnimatePresence>
        {selectedEvent && (
          <PostModal
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
            onDelete={handleDelete}
            onPublishNow={handlePublishNow}
          />
        )}
      </AnimatePresence>
    </PanelLayout>
  );
}
